import isString from 'lodash/isString'
import { createRoot } from 'react-dom/client'
import { isNumeric } from 'validator'
import Subscribe from './Subscribe_'

export type CountryCode = 'CA'

export const isCountryCode = (
  countryCode: unknown
): countryCode is CountryCode => {
  return isString(countryCode) && ['CA'].includes(countryCode)
}

export type InternationalPrice = {
  amount_in_cents: string
  amount_in_dollars: string
  country: string
  country_code: CountryCode
}

export type Rate = {
  allow_promotion_codes: boolean
  description: string
  international_prices: Record<CountryCode, InternationalPrice>
  nid?: string
  name: string
  price_in_cents: string
  price_in_dollars: string
  pricing_note: string | null
}

export type Rates = Record<string, Rate>

export type Recipient = {
  address?: {
    address_line1?: string
    address_line2?: string
    administrative_area?: string
    country_code?: string
    locality?: string
    organization?: string
    postal_code?: string
  }
  delay_welcome_message?: string // Will be unset if not a gift
  delay_until?: string // Will be unset if not a gift
  email?: string // Will be unset if not a gift
  id: string // This is only used client-side
  message?: string // Will be unset if not a gift
  name?: string // Will be unset if not a gift
  phone?: string
  starting_book_number?: string
}

export type Values = {
  is_gift?: string
  purchaser_name?: string
  purchaser_email_address?: string
  rate_nid?: string
  recipients?: Record<string, Recipient>
  referred_by?: string
  referred_by_other?: string
  utms?: Record<string, string>
}

export type ServerErrors = {
  nonce?: string
  renewal_exists?: Record<string, string>
  startable_books?: string
  subscribers_already_exist?: Record<string, string>
}

export type Page =
  | 'SubscriptionRates'
  | 'PurchaserInformation'
  | 'IsThisAGiftForSomeoneElse'
  | 'RecipientsInformation'
  | 'Review'

export type State = {
  giftWasInHash: boolean
  isSaving: boolean
  isSubmitting: boolean
  nonce: string | undefined
  page: Page
  recipientBeingEdited: Recipient | null
  serverErrors: ServerErrors
  values: Values
}

export type Book = {
  number: string
  pathname: string | null
  title: string | null
}

export type Data = {
  auto_charge_terms: string | null
  banner: string | null
  international_pricing_note: string | null
  nonce: string
  rates: Rates
  rates_banner: string | null
  rates_subtitle: string | null
  saved_state: State | null
  startable_books: Book[]
}

const rootEl = document.getElementById('subscribe-root')
if (rootEl?.dataset.initialData) {
  let initialData: Data = JSON.parse(rootEl.dataset.initialData)

  let debug = false
  try {
    const url = new URL(window.location.href)
    if (url.searchParams.get('debug') === '1') {
      console.log('subscribe, initialData = ', initialData)
      debug = true
    } else {
      console.log('subscribe')
    }
  } catch {}

  if (window.fbq) {
    window.fbq('track', 'ViewContent')
    console.log('fbq track ViewContent')
  }

  const root = createRoot(rootEl)

  if (window.location.hash) {
    // A hash in the URL clears server-saved state, because the user has taken
    // a new and specific action (I'm buying a gift, I want this specific rate,
    // I want my subscription to start with a particular book, etc.)
    const hash = window.location.hash.replace(/^#/, '')
    if (isNumeric(hash)) {
      // Pre-select a rate if there's a rate NID in the hash
      initialData = {
        ...initialData,
        saved_state: {
          giftWasInHash: false,
          isSaving: false,
          isSubmitting: false,
          nonce: initialData.nonce,
          page: 'PurchaserInformation',
          recipientBeingEdited: null,
          serverErrors: {},
          values: {
            rate_nid: hash,
          },
        },
      }
    } else if (hash === 'gift') {
      // Switch to gift mode if 'gift' is in the hash
      initialData = {
        ...initialData,
        saved_state: {
          giftWasInHash: true,
          isSaving: false,
          isSubmitting: false,
          nonce: initialData.nonce,
          page: 'SubscriptionRates',
          recipientBeingEdited: null,
          serverErrors: {},
          values: {
            is_gift: '1',
          },
        },
      }
    }
    window.location.hash = ''
  }

  root.render(<Subscribe data={initialData} debug={debug} />)
}
